import defaultRestClient from '@/services/clients/rest'
import _ from 'lodash'
import reusePromise from '@/utils/reusePromise'

async function get (resourceName, resourcePath, restClient) {
  try {
    const response = await restClient.get(resourcePath)
    return response.data
  } catch (error) {
    console.warn(`Error getting ${resourceName}`, error)
    throw error
  }
}
// wrapping get in reusePromise avoids load racing condition.
const getReusedPromise = reusePromise(get)

async function update (resourceName, resourcePath, data, restClient) {
  try {
    const response = await restClient.put(resourcePath, data)
    return response.data
  } catch (error) {
    console.warn(`Error updating ${resourceName}`, error)
    throw error
  }
}

export function registerSingletonResourceWithStore (resourceName, resourcePath, store, options) {
  if (store.state[resourceName]) {
    // might be hot reload in development
    console.warn(`${resourceName} module already registered with store`)
    return
  }

  // Module state is always namespaced and complicates dirty detection,
  // so merge it explicitly.
  const {
    state: extendedState,
    getters: extendedGetters,
    restClient = defaultRestClient,
    dataGetter = data => data,
    supplementalDataGetter = _data => null,
    ...extendedModule
  } = options || {}

  store.registerModule(resourceName, _.merge({
    namespaced: true,
    state () {
      return _.merge({
        loaded: false,
        originalData: {},
        formData: {},
        formInvalid: false,
        supplementalData: null
      }, extendedState && extendedState())
    },
    getters: {
      formDirty: state => {
        return !_.isEqual(state.originalData, state.formData)
      },
      // dirtyFields is for debugging purpose
      dirtyFields: (state, _getters) => {
        const o1 = state.originalData
        const o2 = state.formData

        return _.reduce(o1, (result, value, key) =>
          _.isEqual(value, o2[key]) ? result : result.concat(key), [])
      },
      ...extendedGetters
    },
    actions: {
      async load (context, forceRefresh) {
        if (context.state.loaded && !forceRefresh) {
          return context.state.originalData
        }
        // Although wrapping in reusePromise avoids making multiple
        // remote calls, it doesn't avoid multiple vuex commits.
        // TODO: Avoid multiple commits in racing condition.
        const data = await getReusedPromise(resourceName, resourcePath, restClient)
        const originalData = dataGetter(data)
        const supplementalData = supplementalDataGetter(data)
        context.commit('load', { originalData, supplementalData })
        return originalData
      },
      async update (context) {
        const result = await update(resourceName, resourcePath, context.state.formData, restClient)
        const originalData = dataGetter(result)
        const supplementalData = supplementalDataGetter(result)
        context.commit('update', { originalData, supplementalData })
        return originalData
      },
      cancelChanges (context) {
        context.commit('cancelChanges', context.state.originalData)
      }
    },
    mutations: {
      load (state, { originalData, supplementalData }) {
        Object.assign(state, {
          originalData,
          supplementalData,
          formData: _.cloneDeep(originalData),
          loaded: true
        })
      },
      update (state, { originalData, supplementalData }) {
        Object.assign(state, {
          originalData,
          supplementalData,
          formData: _.cloneDeep(originalData)
        })
      },
      cancelChanges (state) {
        state.formData = _.cloneDeep(state.originalData)
      },
      formInvalidChanged (state, formInvalid) {
        state.formInvalid = formInvalid
      },
      formDataChanged (state, formData) {
        state.formData = formData
      },
    }
  }, extendedModule))
}
